import { collection, endBefore, getCountFromServer, getDocsFromServer, limit, orderBy, query, startAfter } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "../../../firebase";

export const INITIAL_PAGE_SIZE = 25;
export const PAGE_SIZE = 50;
const SORT_FIELDS = ["sortGroup", "sortCompletion", "sortDistance", "sortKey"];
// Only server-managed race readiness selects compact; the build retains a
// project-level isolation/kill switch, never individual user or race lists.
export function isCompactLeaderboardEnabled({ raceData, uid, projectId }, config = {
  projectId: process.env.REACT_APP_COMPACT_LEADERBOARD_PROJECT_ID,
}) {
  const policy = raceData?.leaderboard;
  return Boolean(uid && projectId && config.projectId === projectId &&
    policy?.schemaVersion === 1 && policy.status === "ready" &&
    (policy.mode === "compact" || (policy.mode === "canary" &&
      Array.isArray(policy.userIds) && policy.userIds.includes(uid))));
}

export function compactRow(id, data) {
  if (data.schemaVersion !== 1 || !["team", "individual"].includes(data.entryType) ||
      !Number.isFinite(data.totalDistance) || typeof data.sortKey !== "string" ||
      ![data.sortGroup, data.sortCompletion, data.sortDistance].every(Number.isFinite) ||
      (data.entryType === "team" ? !data.teamName : !data.participantId)) {
    throw new Error("Compact leaderboard entry is not ready");
  }
  return { id, data: { ...data, isTeam: data.entryType === "team", membersCount: data.memberCount } };
}

function orderedEntries(raceId) {
  return query(collection(db, "races", raceId, "leaderboardEntries"), ...SORT_FIELDS.map((field) => orderBy(field, "asc")));
}

export async function loadCompactPage(raceId, cursor = null) {
  const pageSize = cursor ? PAGE_SIZE : INITIAL_PAGE_SIZE;
  const ordered = orderedEntries(raceId);
  const snapshot = await getDocsFromServer(query(ordered, ...(cursor ? [startAfter(cursor)] : []), limit(pageSize + 1)));
  const docs = snapshot.docs.slice(0, pageSize);
  if (!docs.length) throw new Error("Compact leaderboard page is unavailable");
  // Recount the offset when paging: live updates can move rows across a cursor.
  const startRank = cursor ? (await getCountFromServer(query(ordered, endBefore(docs[0])))).data().count + 1 : 1;
  return {
    rows: docs.map((doc) => compactRow(doc.id, doc.data())),
    cursor: docs[docs.length - 1],
    hasNext: snapshot.docs.length > pageSize,
    startRank,
    documentReads: snapshot.docs.length,
  };
}

export async function loadCurrentRank(raceId) {
  const result = await httpsCallable(getFunctions(app), "getCompactLeaderboardRank")({ raceId });
  const { rank, totalEntries, entry } = result.data;
  if (!Number.isInteger(rank) || rank < 1 || !Number.isInteger(totalEntries) || totalEntries < rank || !entry) {
    throw new Error("Current leaderboard rank is unavailable");
  }
  return { rank, totalEntries, row: compactRow(entry.id, entry.data) };
}

export async function loadCompactMap(raceId) {
  const snapshot = await getDocsFromServer(orderedEntries(raceId));
  if (snapshot.empty) throw new Error("Map leaderboard is unavailable");
  return new Map(snapshot.docs.map((doc) => {
    const row = compactRow(doc.id, doc.data());
    return [row.data.isTeam ? row.data.teamName : row.data.participantId, row.data];
  }));
}
